import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal, message } from 'antd';
import {
  ProductOutput,
  ProductStatus,
  useDeleteProductMutation,
} from 'gql/types';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type PropTypes = {
  product: ProductOutput;
  onDelete?: (affected: boolean) => void;
  onOpenPopconfirm?: () => void;
  children?: ReactNode;
};

const DeleteProductButton: FC<PropTypes> = ({
  children,
  product,
  onDelete,
  onOpenPopconfirm,
}) => {
  const { t } = useTranslation(
    'products.ProductDropdownActions.DeleteProductButton'
  );

  const isProductCollected = product.status === ProductStatus.Collected;
  const [deleteProduct, deleteProductMutation] = useDeleteProductMutation({
    refetchQueries: ['getProducts'],
    variables: {
      input: {
        id: product.id,
      },
    },
    onCompleted: () => {
      message.success(t('productHasBeenDeleted'));
      onDelete?.(true);
    },
    onError: (error) => {
      message.error(error.message);
      onDelete?.(false);
    },
  });

  const showDeleteConfirm = () => {
    onOpenPopconfirm?.();

    Modal.confirm({
      title: t('sureToDeleteProduct'),
      icon: <ExclamationCircleOutlined />,
      okText: t('yes'),
      cancelText: t('cancel'),
      okButtonProps: {
        danger: true,
        loading: deleteProductMutation.loading,
      },
      onOk: async () => {
        await deleteProduct();
      },
    });
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isProductCollected) {
      showDeleteConfirm();
    }
  };

  return (
    <span
      onClick={handleClick}
      style={{ cursor: isProductCollected ? 'pointer' : 'not-allowed' }}
    >
      {children || t('deleteProduct')}
    </span>
  );
};

export default DeleteProductButton;
